/* eslint-disable no-unused-vars */
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";

import { ampli } from "../../../../ampli";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import Alert, { AlertType } from "../../../../components/common/WaffyAlert";
import { IApiResponse } from "../../../../setup/api/models";
// api
import {
  requestOtp2,
  RequestOtpBody,
  validateOtpGeneric,
} from "../../../../setup/api/services/otp";
import { EOTPType } from "../../../../setup/api/services/otp/models";
import { updateProfileInfo } from "../../../../setup/api/services/profile";
// types
import {
  IMyInfoData,
  IUpdateProfileInfoRequestBody,
} from "../../../../setup/api/services/profile/models";
// api
import {
  postUserPinCode,
  updateNationalId,
} from "../../../../setup/api/services/user";
import { IRegisterByPhoneResponseData } from "../../../../setup/api/services/user/models";
import { fetchAndSetMyInfo, loginUser } from "../../../../setup/context/api";
// provider and context
import {
  contextUserLogin,
  fetchUserDependencies,
} from "../../../../setup/context/authContext";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
// helpers
import { returnError } from "../../../../setup/util/errorUtil";
import { IVerticalSignupStepperProps } from "../../models";
import { PhoneNumberVerification } from "../../phoneNumberVerification";
import { SecurityCodeForm } from "../../securityPinCode/securityCodeForm";
// form validations
import {
  validateName,
  validateNationalId,
  validateOtpCode,
  validateSecurityPinCode,
} from "../../validation";
import { AbsherButtons } from "../absherButtons";
import { AbsherStep } from "../absherStep";
// constant
import {
  IF_NOT_RECEIVED_OTP,
  RESEND,
  TIMER_VALUE_10,
  TIMER_VALUE_30,
  TIMER_VALUE_60,
  TIMER_VALUE_300,
} from "../constants";
import { ISteps } from "../models";
// components
import { ProfileInfoForm } from "../profileInfoForm";
import Timer from "./timer";
// constants
// styles
import styles from "./verticalStepper.module.scss";

export const SignupStepper = ({
  authAction,
  registerationError,
  registeredUser,
  publicContractLogin,
}: IVerticalSignupStepperProps) => {
  const userProfile = useMemo(() => AuthConfig.getMyInfo(), []);
  // states
  const [error, setError] = useState<string>();

  const [resendOtp, setResendOtp] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(TIMER_VALUE_10);

  const [phoneVerified, setPhoneVerified] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [otpCode, setOtpCode] = useState<string>();
  const [pinCode, setPinCode] = useState<string>();
  const [profileInfo, setProfileInfo] =
    useState<IUpdateProfileInfoRequestBody>();
  const [absherOtpCode, setAbhserOtpCode] = useState<string>();
  const [nationalId, setNationalId] = useState<string>();
  const [isValidNationalId, setisValidNationalId] = useState<boolean>(false);
  const [numberOfOtp, setNumberOfOtp] = useState<number>(0);
  const [enableBiometric, setEnableBiometric] = useState<boolean>(false);
  const { t } = useTranslation();

  const onPinCodeChange = useCallback((event: string) => {
    setPinCode(event);
  }, []);
  const onOtpCodeChange = useCallback((event: string) => {
    setOtpCode(event);
  }, []);
  const onPhoneNumberChange = useCallback((event: string) => {
    setPhoneNumber(event);
  }, []);
  const onProfileInfoChange = useCallback((event: any) => {
    setProfileInfo(event);
  }, []);
  const onNationalIdChange = useCallback((event: string) => {
    setNationalId(event);
  }, []);
  const onAbhserOtpChange = useCallback((event: string) => {
    setAbhserOtpCode(event);
    setNumberOfOtp(numberOfOtp + 1);
  }, []);

  useEffect(() => {
    setError(registerationError);
  }, [registerationError]);
  useEffect(() => {
    if (userProfile !== null || registeredUser) {
      try {
        const id = userProfile?.id || registeredUser?.id;
        if (id) {
          ampli.client.setUserId(id?.toString());
          ampli.viewedSignUpPage();
        }
        setPhoneVerified(true);
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [registeredUser, userProfile]);

  const handleNext = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(undefined);
  }, []);

  const sendAmpliEventVerifyAbsher = () => {
    ampli.verifiedViaAbsher({
      absherPageSource: "signUpPage", // enum,
      resendAbsherOtpTrials: 0, // number
    });
  };
  const sendAmpliEeventSignedUp = useCallback(() => {
    const myInfo = AuthConfig.getMyInfo();
    if (myInfo) {
      ampli.client.setUserId(myInfo.id.toString() || "");
      ampli.signedUp({
        isAbsherVerified: false, // boolean,
        userPhoneNumber: myInfo?.phoneNumber || "", // string,
        resendRegistrationOtpTrials: numberOfOtp, // number,
        userName: myInfo.profile.fullName || "", // string,
        enableBiometric: false,
        signUpDate: new Date().toISOString(),
      });
    }
  }, [numberOfOtp]);

  const onEnd = useCallback(async () => {
    handleNext();
    await fetchAndSetMyInfo();
    authAction();
  }, [authAction, handleNext]);

  const sendAmpliUserPropertiesForAbsherVerification = useCallback(
    (registeredUser) => {
      ampli.client.setUserId(registeredUser.id);
      ampli.client.setUserProperties({
        userId: registeredUser.id,
        isAbsherVerified: true,
        absherVerificationDate: new Date().toISOString(),
      });
    },
    []
  );
  const onSubmitAbsherOtp = useCallback(
    (registeredUser: IRegisterByPhoneResponseData, otpCode: string) => {
      if (registeredUser.id && otpCode && validateOtpCode(otpCode)) {
        validateOtpGeneric({
          otp: Number.parseInt(otpCode),
          type: EOTPType.ABSHER_OTP,
          phoneNumber: registeredUser?.phoneNumber,
        })
          .then((res) => {
            if (res?.data?.valid) {
              sendAmpliUserPropertiesForAbsherVerification(registeredUser);
              sendAmpliEventVerifyAbsher();
              onEnd();
            } else setError(t("lbl_error_otp_validation"));
          })
          .catch((error) => {
            Sentry.captureException(error);
            setError(error?.message);
          });
      } else {
        setError(t("ERROR_OTP_VALIDATION"));
      }
    },
    [onEnd, sendAmpliUserPropertiesForAbsherVerification, t]
  );

  const onSubmitnationalId = useCallback(
    (registeredUser: IRegisterByPhoneResponseData, nationalId: string) => {
      if (registeredUser && nationalId && validateNationalId(nationalId)) {
        updateNationalId(registeredUser.id, { nationalId })
          .then(() => {
            setisValidNationalId(true);
          })
          .catch((error: IApiResponse) => {
            Sentry.captureException(error);
            setisValidNationalId(false);
            setError(error.error.message);
          });
      } else {
        setError(t("lbl_error_absher_validation"));
      }
    },
    [t]
  );

  const onAbsherFormSubmit = useCallback(
    ({ registeredUser, nationalId, absherOtpCode, isValidNationalId }) => {
      if (isValidNationalId === false)
        onSubmitnationalId(registeredUser, nationalId);
      else onSubmitAbsherOtp(registeredUser, absherOtpCode);
    },

    [onSubmitAbsherOtp, onSubmitnationalId]
  );

  const onOtpCodeFormSubmit = useCallback(
    ({ otpCode, registeredUser }) => {
      if (registeredUser && otpCode && validateOtpCode(otpCode)) {
        validateOtpGeneric({
          otp: Number.parseInt(otpCode),
          type: EOTPType.WAFFY_OTP,
          phoneNumber: registeredUser?.phoneNumber,
        })
          .then((res) => {
            ampli && ampli?.validatedOtp({ isOtpValid: res?.data?.valid });
            if (res.data.valid) {
              handleNext();
            } else setError(t("lbl_error_otp_validation"));
          })
          .catch((error) => {
            Sentry.captureException(error);
            setError("lbl_otp_form_error");
          });
      } else setError(t("lbl_error_otp_validation"));
    },
    [handleNext, t]
  );

  const displayResendOtp = useCallback(() => {
    setResendOtp(true);
    setNumberOfOtp(numberOfOtp + 1);
    if (numberOfOtp === 0) {
      setTimer(TIMER_VALUE_30);
    } else if (numberOfOtp === 1) {
      setTimer(TIMER_VALUE_60);
    } else {
      setTimer(TIMER_VALUE_300);
    }
  }, [numberOfOtp]);

  const sendAmplitudePersonalInfo = useCallback((profileInfo) => {
    ampli &&
      ampli?.addedPersonalInfo({
        firtName: profileInfo.firstName,
        lastName: profileInfo.lastName,
        uploadedPersonalImage: profileInfo?.photoUrl ? true : false,
      });
  }, []);

  const login = useCallback(
    (registeredUser: IRegisterByPhoneResponseData, pinCode: string) => {
      const formData = new FormData();
      formData.append("username", registeredUser.phoneNumber);
      formData.append("password", pinCode);
      loginUser(formData).then(async (res) => {
        contextUserLogin(res);
        fetchUserDependencies();
      });
    },
    []
  );

  const onPinCodeFormSubmit = useCallback(
    ({ registeredUser, pinCode }) => {
      if (registeredUser && pinCode && validateSecurityPinCode(pinCode)) {
        postUserPinCode(registeredUser.id, { pinCode: pinCode })
          .then(() => {
            ampli && ampli?.addedPincode();
            const myInfo = AuthConfig.getMyInfo();
            sendAmpliEeventSignedUp();
            sendAmpliUserProperties(myInfo);
            login(registeredUser, pinCode);
            handleNext();
          })
          .catch((error) => {
            Sentry.captureException(error);
            setError(returnError(error) || t("lbl_security_code_form_error"));
          });
      } else setError(t("lbl_error_pincode_validation"));
    },
    [handleNext, login, sendAmpliEeventSignedUp, t]
  );

  const onProfileInfoFormSubmit = useCallback(
    ({ registeredUser, profileInfo }) => {
      if (
        registeredUser &&
        profileInfo.firstName &&
        profileInfo.lastName &&
        validateName(profileInfo.firstName) &&
        validateName(profileInfo.firstName)
      )
        updateProfileInfo(registeredUser.id, { ...profileInfo })
          .then((res) => {
            sendAmplitudePersonalInfo(profileInfo);
            AuthConfig.setMyInfo(res.data);
            setEnableBiometric(res.data.profile.biometricEnabled || false);
            handleNext();
          })
          .catch((error) => {
            Sentry.captureException(error);
            setError(returnError(error));
          });
      else setError(t("lbl_error_profile_validation"));
    },
    [handleNext, sendAmplitudePersonalInfo, t]
  );

  const requestPhoneOtp = useCallback(async () => {
    try {
      const body: RequestOtpBody = {
        phoneNumber: `+${phoneNumber}`,
        notificationChannel: "SMS",
      };
      await requestOtp2(body);
    } catch (error) {
      Sentry.captureException(error);
    }
    setResendOtp(false);
  }, [phoneNumber]);

  const steps = useMemo(() => {
    const stepperSteps: ISteps[] = [
      {
        label: t("lbl_otp_form_title"),
        description: (
          <div className={styles.description}>
            <p className={styles.timer_text}>
              {t("lbl_enter_the_verification_code")}
              <span dir="ltr" className={classNames("subtitle1", "mr1")}>
                {phoneNumber}
              </span>
            </p>
          </div>
        ),
        extraData: resendOtp ? (
          <div className={styles.mobile_link} onClick={() => requestPhoneOtp()}>
            <p className={"p2"}>{IF_NOT_RECEIVED_OTP}</p>
            <p className={classNames("subtitle2", "primary", "mr1", "pointer")}>
              {RESEND}{" "}
            </p>
          </div>
        ) : (
          <Timer timerValue={timer} callBack={displayResendOtp}>
            {IF_NOT_RECEIVED_OTP}
          </Timer>
        ),
        onSubmit: onOtpCodeFormSubmit,
        component: (
          <PhoneNumberVerification
            phoneVerified={phoneVerified}
            onPhoneNumberChange={onPhoneNumberChange}
            phoneNumber={phoneNumber}
            onOtpCodeChange={onOtpCodeChange}
            otpCode={otpCode}
          />
        ),
      },
      {
        label: t("lbl_complete_profile_title"),
        description: t("lbl_step1_signup_description"),
        onSubmit: onProfileInfoFormSubmit,
        component: (
          <ProfileInfoForm
            userId={registeredUser?.id}
            key={1}
            onChange={onProfileInfoChange}
            value={profileInfo}
          />
        ),
      },
      {
        label: t("lbl_steps2_label"),
        description: t("lbl_steps2_signup_description"),
        onSubmit: onPinCodeFormSubmit,
        component: (
          <SecurityCodeForm
            key={2}
            onChange={onPinCodeChange}
            value={pinCode}
          />
        ),
      },
      {
        label: t("lbl_slider_1_confirm_itentiy_by_absher_title"),
        description: t("lbl_confirm_identity_sub_title"),
        onSubmit: onAbsherFormSubmit,
        component: (
          <AbsherStep
            key={3}
            onNationalIdChange={onNationalIdChange}
            onOtpChange={onAbhserOtpChange}
            isValidNationalId={isValidNationalId}
          />
        ),
      },
    ];
    return stepperSteps;
  }, [
    displayResendOtp,
    isValidNationalId,
    onAbhserOtpChange,
    onAbsherFormSubmit,
    onNationalIdChange,
    onOtpCodeChange,
    onOtpCodeFormSubmit,
    onPhoneNumberChange,
    onPinCodeChange,
    onPinCodeFormSubmit,
    onProfileInfoChange,
    onProfileInfoFormSubmit,
    otpCode,
    phoneNumber,
    phoneVerified,
    pinCode,
    profileInfo,
    registeredUser?.id,
    requestPhoneOtp,
    resendOtp,
    t,
  ]);

  const onSubmit = useCallback(
    (index: number) => {
      if (steps) {
        steps[index].onSubmit({
          phoneVerified,
          phoneNumber,
          otpCode,
          registeredUser,
          pinCode,
          profileInfo,
          nationalId,
          absherOtpCode,
          isValidNationalId,
        });
      }
    },
    [
      absherOtpCode,
      isValidNationalId,
      nationalId,
      otpCode,
      phoneNumber,
      phoneVerified,
      pinCode,
      profileInfo,
      registeredUser,
      steps,
    ]
  );

  const sendAmpliUserProperties = (res: IMyInfoData) => {
    ampli.client.setUserId(res.id.toString());
    ampli.client.setUserProperties({
      userId: res.id,
      signupDate: new Date().toISOString(),
      userName: res.profile.fullName || "",
      userPhoneNumber: res.phoneNumber,
      isAbsherVerified: false,
    });
  };

  const renderStepperAction = useCallback(
    (index: number) => {
      return steps && index === steps?.length - 1 ? (
        <AbsherButtons onSkip={onEnd} onConfirm={() => onSubmit(index)} />
      ) : (
        <div className={styles["stepper_action_button"]}>
          <PrimaryButton
            id={`signup_step_${index + 1}`}
            onClick={() => onSubmit(index)}>
            {t("lbl_continue")}
          </PrimaryButton>
        </div>
      );
    },
    [onEnd, onSubmit, steps, t]
  );

  return (
    <div className={styles["stepper_container"]}>
      {steps && (
        <div
          className={classNames(
            styles.steps_container,
            !publicContractLogin && (activeStep == 0 ? styles.p60 : styles.p30)
          )}>
          {error && (
            <Alert type={AlertType.ERROR} showIcon>
              {error}
            </Alert>
          )}
          {steps.map((step: ISteps, index: number) => (
            <div className={styles.steps_wrapper} key={step.label}>
              <div className={styles.step_container}>
                <div
                  style={{ marginLeft: "1rem" }}
                  className={classNames(
                    styles.step,
                    activeStep > index
                      ? styles.completed
                      : activeStep === index
                      ? styles.selected
                      : styles.default
                  )}>
                  {activeStep > index ? (
                    <img src="/assets/images/svg/checkMark.svg" alt="check" />
                  ) : (
                    <p
                      className={classNames(
                        "p1",
                        activeStep > index - 1 ? "white" : "g600"
                      )}>
                      {index + 1}
                    </p>
                  )}
                </div>
                {index === activeStep ? (
                  <h1 className="g800">{step?.label}</h1>
                ) : (
                  <p className={classNames("p1", "g400")}>{step?.label}</p>
                )}
              </div>

              <div className={styles.vertical_stepper}>
                {activeStep === index && (
                  <>
                    <p className={classNames("subtitle1", "g700", "mt12")}>
                      {step.description}
                    </p>
                    {step.component}
                    {step?.extraData && (
                      <p className={styles.extra_step}>{step?.extraData}</p>
                    )}
                    {renderStepperAction(index)}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignupStepper;
